import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { GitCompare, Loader2 } from 'lucide-react';
import {
  useOptimizationRun,
  useOptimizationRuns,
  type OptimizationRunDetail,
} from '@/hooks/optimization/useOptimizationRuns';

interface CompareRunsDialogProps {
  currentRun: OptimizationRunDetail;
  open: boolean;
  onClose: () => void;
}

function formatKzt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(n);
}

function pct(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `${(n * 100).toFixed(0)}%`;
}

/**
 * Метрики двух runs side-by-side. Дельта подсвечивается: для ФОТ и
 * недокрытия меньше — лучше, для покрытия больше — лучше.
 */
const DeltaCell: React.FC<{
  label: string;
  a: number | null | undefined;
  b: number | null | undefined;
  lessIsBetter?: boolean;
  unit?: string;
  format?: 'kzt' | 'pct' | 'num';
}> = ({ label, a, b, lessIsBetter = true, unit = '', format = 'num' }) => {
  const fmt = (v: number | null | undefined) =>
    v === null || v === undefined
      ? '—'
      : format === 'kzt'
      ? `${formatKzt(v)} ₸`
      : format === 'pct'
      ? pct(v)
      : `${v}${unit}`;
  let delta: number | null = null;
  if (a !== null && a !== undefined && b !== null && b !== undefined) {
    delta = b - a;
  }
  const isBetter =
    delta !== null && (lessIsBetter ? delta < 0 : delta > 0);
  const isWorse =
    delta !== null && delta !== 0 && (lessIsBetter ? delta > 0 : delta < 0);
  const deltaColor = isBetter
    ? 'text-green-700'
    : isWorse
    ? 'text-red-700'
    : 'text-gray-500';
  return (
    <tr className="border-b">
      <td className="px-2 py-1.5 text-sm">{label}</td>
      <td className="px-2 py-1.5 text-right text-sm font-mono">{fmt(a)}</td>
      <td className="px-2 py-1.5 text-right text-sm font-mono">{fmt(b)}</td>
      <td className={`px-2 py-1.5 text-right text-sm font-mono ${deltaColor}`}>
        {delta === null
          ? '—'
          : format === 'pct'
          ? `${delta > 0 ? '+' : ''}${(delta * 100).toFixed(1)} п.п.`
          : format === 'kzt'
          ? `${delta > 0 ? '+' : ''}${formatKzt(delta)} ₸`
          : `${delta > 0 ? '+' : ''}${delta}${unit}`}
      </td>
    </tr>
  );
};

export const CompareRunsDialog: React.FC<CompareRunsDialogProps> = ({
  currentRun,
  open,
  onClose,
}) => {
  const { data: allRuns } = useOptimizationRuns(currentRun.location_id);
  const [otherId, setOtherId] = useState<string>('');
  const { data: otherRun } = useOptimizationRun(otherId || null);

  const candidates = useMemo(
    () =>
      (allRuns ?? []).filter(
        (r) => r.id !== currentRun.id && r.status === 'completed',
      ),
    [allRuns, currentRun.id],
  );

  // Diff в назначениях: считаем дни-разницу на сотрудника
  const assignmentsDiff = useMemo(() => {
    const a = currentRun.solution?.assignments ?? [];
    const b = otherRun?.solution?.assignments ?? [];
    if (b.length === 0) return null;
    const byEmpA = new Map<string, Set<string>>();
    const byEmpB = new Map<string, Set<string>>();
    for (const x of a) {
      const s = byEmpA.get(x.employee_id) ?? new Set<string>();
      s.add(x.date);
      byEmpA.set(x.employee_id, s);
    }
    for (const x of b) {
      const s = byEmpB.get(x.employee_id) ?? new Set<string>();
      s.add(x.date);
      byEmpB.set(x.employee_id, s);
    }
    const empIds = new Set<string>([...byEmpA.keys(), ...byEmpB.keys()]);
    const rows: Array<{ empId: string; a: number; b: number; delta: number }> =
      [];
    for (const id of empIds) {
      const sA = byEmpA.get(id)?.size ?? 0;
      const sB = byEmpB.get(id)?.size ?? 0;
      rows.push({ empId: id, a: sA, b: sB, delta: sB - sA });
    }
    rows.sort((x, y) => Math.abs(y.delta) - Math.abs(x.delta));
    return rows;
  }, [currentRun, otherRun]);

  const mA = currentRun.solution?.metrics ?? {};
  const mB = otherRun?.solution?.metrics ?? {};

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl">
        <DialogClose onClose={onClose} />
        <DialogHeader>
          <DialogTitle>
            <span className="inline-flex items-center gap-2">
              <GitCompare className="h-5 w-5 text-purple-600" />
              Сравнение запусков
            </span>
          </DialogTitle>
          <DialogDescription>
            Side-by-side метрики и diff по дням сотрудников.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Сравнить с</Label>
            <Select value={otherId} onValueChange={setOtherId}>
              <SelectTrigger>
                <SelectValue placeholder="Выберите run этой же локации" />
              </SelectTrigger>
              <SelectContent>
                {candidates.length === 0 && (
                  <SelectItem value="__none__" disabled>
                    Нет других completed runs
                  </SelectItem>
                )}
                {candidates.map((r) => {
                  const tag = r.parent_run_id ? ' [what-if]' : '';
                  const dt = new Date(r.created_at).toLocaleString('ru-RU', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  });
                  return (
                    <SelectItem key={r.id} value={r.id}>
                      {dt} · {r.period_start}→{r.period_end}
                      {tag} · ФОТ {formatKzt(r.total_fot)}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {otherId && !otherRun && (
            <div className="text-center py-6">
              <Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" />
            </div>
          )}

          {otherRun && (
            <>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <RunHeaderCard label="A · текущий" run={currentRun} />
                <RunHeaderCard label="B · сравниваемый" run={otherRun} />
              </div>

              <div className="border rounded-md">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-1.5 text-left text-xs font-medium">
                        Метрика
                      </th>
                      <th className="px-2 py-1.5 text-right text-xs font-medium">
                        A
                      </th>
                      <th className="px-2 py-1.5 text-right text-xs font-medium">
                        B
                      </th>
                      <th className="px-2 py-1.5 text-right text-xs font-medium">
                        Δ (B − A)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <DeltaCell
                      label="ФОТ"
                      a={mA.total_fot}
                      b={mB.total_fot}
                      format="kzt"
                      lessIsBetter
                    />
                    <DeltaCell
                      label="Покрытие"
                      a={mA.coverage_score}
                      b={mB.coverage_score}
                      format="pct"
                      lessIsBetter={false}
                    />
                    <DeltaCell
                      label="Недокрытие"
                      a={mA.understaffed_hours}
                      b={mB.understaffed_hours}
                      unit=" ч"
                      lessIsBetter
                    />
                    <DeltaCell
                      label="Переработки"
                      a={mA.overtime_hours}
                      b={mB.overtime_hours}
                      unit=" ч"
                      lessIsBetter
                    />
                  </tbody>
                </table>
              </div>

              {assignmentsDiff && assignmentsDiff.length > 0 && (
                <div>
                  <div className="text-sm font-medium mb-1.5">
                    Топ-30 сотрудников по разнице в днях
                  </div>
                  <div className="border rounded-md max-h-[280px] overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-gray-50">
                        <tr>
                          <th className="px-2 py-1.5 text-left">Сотрудник</th>
                          <th className="px-2 py-1.5 text-right">A дн.</th>
                          <th className="px-2 py-1.5 text-right">B дн.</th>
                          <th className="px-2 py-1.5 text-right">Δ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assignmentsDiff.slice(0, 30).map((r) => (
                          <tr key={r.empId} className="border-t">
                            <td className="px-2 py-1 font-mono">
                              {r.empId.slice(0, 8)}…
                            </td>
                            <td className="px-2 py-1 text-right">{r.a}</td>
                            <td className="px-2 py-1 text-right">{r.b}</td>
                            <td
                              className={`px-2 py-1 text-right ${
                                r.delta > 0
                                  ? 'text-green-700'
                                  : r.delta < 0
                                  ? 'text-red-700'
                                  : 'text-gray-500'
                              }`}
                            >
                              {r.delta > 0 ? `+${r.delta}` : r.delta}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {(currentRun.llm_explanation || otherRun.llm_explanation) && (
                <div className="grid grid-cols-2 gap-3">
                  <ExplanationCard label="A" text={currentRun.llm_explanation} />
                  <ExplanationCard label="B" text={otherRun.llm_explanation} />
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Закрыть
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RunHeaderCard: React.FC<{
  label: string;
  run: OptimizationRunDetail;
}> = ({ label, run }) => {
  const dt = new Date(run.created_at).toLocaleString('ru-RU', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
  return (
    <div className="border rounded-md p-2 bg-gray-50">
      <div className="font-medium flex items-center gap-2">
        {label}
        {run.parent_run_id && (
          <Badge
            variant="outline"
            className="text-[9px] bg-purple-50 border-purple-200 text-purple-700"
          >
            what-if
          </Badge>
        )}
      </div>
      <div className="text-muted-foreground">
        {run.period_start} → {run.period_end}
      </div>
      <div className="text-muted-foreground">Создан: {dt}</div>
      <div className="text-muted-foreground">
        Сценарий: {run.scenario_code} · solver: {run.solver_status ?? '—'}
      </div>
    </div>
  );
};

const ExplanationCard: React.FC<{
  label: string;
  text: string | null;
}> = ({ label, text }) => (
  <div className="border rounded-md p-2 bg-purple-50/40">
    <div className="text-xs font-medium text-purple-900 mb-1">
      Объяснение AI · {label}
    </div>
    <div className="text-xs text-purple-900 whitespace-pre-wrap leading-relaxed max-h-[200px] overflow-y-auto">
      {text ?? '— нет —'}
    </div>
  </div>
);
